import React, { useState } from "react";
import API from "../utils/api";

const AdminSendNotification = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("everyone"); // "everyone" | "single"
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: "success"|"error", text }

  const handleSend = async () => {
    setResult(null);

    if (!title.trim() || !body.trim()) {
      setResult({ type: "error", text: "Please enter both a title and a message." });
      return;
    }

    if (target === "single" && !email.trim()) {
      setResult({ type: "error", text: "Please enter the user's email." });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        title: title.trim(),
        body: body.trim(),
      };
      // Only include email when targeting a single user
      if (target === "single") payload.email = email.trim();

      const res = await API.post("/admin/send-push", payload);

      setResult({
        type: "success",
        text: res.data?.message || "✅ Notification sent successfully!",
      });
      // Clear the form on success
      setTitle("");
      setBody("");
      setEmail("");
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to send notification.";
      setResult({ type: "error", text: `❌ ${msg}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white p-6 flex items-start justify-center">
      <div className="w-full max-w-lg bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl mt-6">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="text-4xl mb-2">🔔</div>
          <h1 className="text-2xl font-bold">Send Notification</h1>
          <p className="text-gray-400 text-sm mt-1">
            Push a notification to your users' phones
          </p>
        </div>

        {/* Result message */}
        {result && (
          <div
            className={`mb-5 p-4 rounded-xl text-sm text-center border ${
              result.type === "success"
                ? "bg-green-500/10 border-green-400/40 text-green-300"
                : "bg-red-500/10 border-red-400/40 text-red-300"
            }`}
          >
            {result.text}
          </div>
        )}

        {/* Target choice */}
        <label className="block text-sm text-gray-400 mb-2">Send to</label>
        <div className="flex gap-3 mb-5">
          <button
            type="button"
            onClick={() => setTarget("everyone")}
            className={`flex-1 py-3 rounded-xl font-semibold text-sm transition ${
              target === "everyone"
                ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg"
                : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
            }`}
          >
            👥 Everyone
          </button>
          <button
            type="button"
            onClick={() => setTarget("single")}
            className={`flex-1 py-3 rounded-xl font-semibold text-sm transition ${
              target === "single"
                ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg"
                : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
            }`}
          >
            👤 One User
          </button>
        </div>

        {/* Email field (only when single) */}
        {target === "single" && (
          <div className="mb-5">
            <label className="block text-sm text-gray-400 mb-2">
              User's email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full p-3 rounded-xl bg-black/40 border border-white/10 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/30 outline-none text-white"
            />
          </div>
        )}

        {/* Title */}
        <div className="mb-5">
          <label className="block text-sm text-gray-400 mb-2">
            Notification Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. New Feature Available!"
            maxLength={60}
            className="w-full p-3 rounded-xl bg-black/40 border border-white/10 focus:border-green-400 focus:ring-2 focus:ring-green-400/30 outline-none text-white"
          />
          <p className="text-xs text-gray-500 mt-1">{title.length}/60</p>
        </div>

        {/* Body */}
        <div className="mb-6">
          <label className="block text-sm text-gray-400 mb-2">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message here..."
            rows={4}
            maxLength={150}
            className="w-full p-3 rounded-xl bg-black/40 border border-white/10 focus:border-green-400 focus:ring-2 focus:ring-green-400/30 outline-none text-white resize-none"
          />
          <p className="text-xs text-gray-500 mt-1">{body.length}/150</p>
        </div>

        {/* Live preview */}
        {(title || body) && (
          <div className="mb-6">
            <p className="text-xs text-gray-400 mb-2">Preview:</p>
            <div className="p-4 rounded-xl bg-zinc-800 border border-zinc-700 flex gap-3 items-start">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                🏦
              </div>
              <div className="min-w-0">
                <p className="font-bold text-white text-sm truncate">
                  {title || "Notification Title"}
                </p>
                <p className="text-gray-300 text-sm break-words">
                  {body || "Your message will appear here"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={loading}
          className={`w-full py-4 rounded-xl font-bold text-base transition-all shadow-lg ${
            loading
              ? "bg-gray-600 cursor-not-allowed text-gray-300"
              : "bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 text-white active:scale-[0.98]"
          }`}
        >
          {loading ? "Sending..." : "📨 Send Notification"}
        </button>
      </div>
    </div>
  );
};

export default AdminSendNotification;
